import { useApolloClient } from "@apollo/client";
import ActionDialog from "@dashboard/components/ActionDialog";
import {
  WsmVariantsBySkuDocument,
  WsmVariantsBySkuQuery,
  WsmVariantsBySkuQueryVariables,
} from "@dashboard/graphql";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { Box, Button, Input, Text, Textarea } from "@saleor/macaw-ui-next";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { pasteMessages } from "../../messages";
import { parsePasteInput, PASTE_ROW_CAP, summarisePaste } from "../../paste";
import { WsmTierRowDraft } from "../../types";
import { chunk, resolvePasteRows, SKU_LOOKUP_CHUNK } from "../../utils";

export interface WsmDealerPasteDialogProps {
  open: boolean;
  /** Rows already on the grid: a re-paste replaces its own break, never doubles it. */
  existingRows: WsmTierRowDraft[];
  onClose: () => void;
  onConfirm: (rows: WsmTierRowDraft[], replacedKeys: ReadonlySet<string>) => void;
}

interface ResolvedVariant {
  id: string;
  name: string;
  sku: string;
  productName: string;
}

const reasonMessage = {
  columns: pasteMessages.reasonColumns,
  sku: pasteMessages.reasonSku,
  amount: pasteMessages.reasonAmount,
  unknownSku: pasteMessages.reasonUnknownSku,
};

/** How long the merchant has to stop typing before the SKUs are looked up. */
const LOOKUP_DEBOUNCE_MS = 400;

export const WsmDealerPasteDialog = ({
  open,
  existingRows,
  onClose,
  onConfirm,
}: WsmDealerPasteDialogProps) => {
  const intl = useIntl();
  const [text, setText] = useState("");
  const [minQuantity, setMinQuantity] = useState("1");
  const client = useApolloClient();
  const parsed = useMemo(() => parsePasteInput(text), [text]);
  /**
   * The exact set of SKUs this paste asks about, carried as a list and never
   * re-split out of the key: a SKU with a space in it used to be torn in half on
   * the way to the lookup and come back unknown.
   */
  const skus = useMemo(
    () => Array.from(new Set(parsed.rows.map(row => row.sku))),
    [parsed.rows],
  );
  /**
   * The lookup's answer is kept under this key, so an answer for a paste the
   * merchant has since edited can never be counted as ready. A newline is safe
   * as the separator because the parser split the paste on newlines, so no SKU
   * can hold one.
   */
  const skuKey = useMemo(() => skus.join("\n"), [skus]);
  const [resolved, setResolved] = useState<{
    key: string;
    variants: Map<string, ResolvedVariant>;
  } | null>(null);
  const [resolving, setResolving] = useState(false);
  /**
   * The paste whose lookup failed. Held so the effect does not spin on a broken
   * network, and so the dialog can say so instead of claiming "0 prices found".
   */
  const [lookupFailed, setLookupFailed] = useState<string | null>(null);
  const known = resolved?.key === skuKey ? resolved.variants : null;

  /**
   * SKUs resolve in the SAME pass as the number check, while the merchant is
   * still looking at the dialog. Resolving them only on the way to the grid is
   * what let unmatched lines be counted as ready and then disappear.
   */
  useEffect(() => {
    if (!open || known || !skuKey || lookupFailed === skuKey) {
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setResolving(true);

      try {
        const pages = await Promise.all(
          chunk(skus, SKU_LOOKUP_CHUNK).map(page =>
            client.query<WsmVariantsBySkuQuery, WsmVariantsBySkuQueryVariables>({
              query: WsmVariantsBySkuDocument,
              variables: { skus: page },
              fetchPolicy: "no-cache",
            }),
          ),
        );

        if (cancelled) {
          return;
        }

        setResolved({
          key: skuKey,
          variants: new Map(
            pages
              .flatMap(page => mapEdgesToItems(page.data?.productVariants) ?? [])
              .filter(variant => !!variant.sku)
              .map(variant => [
                variant.sku as string,
                {
                  id: variant.id,
                  name: variant.name,
                  sku: variant.sku as string,
                  productName: variant.product.name,
                },
              ]),
          ),
        });
      } catch (error) {
        // A refused or dropped lookup is NOT "no SKUs matched". Saying so, and
        // remembering which paste it was, is what makes the retry possible.
        if (!cancelled) {
          setLookupFailed(skuKey);
        }
      } finally {
        if (!cancelled) {
          setResolving(false);
        }
      }
    }, LOOKUP_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, known, skuKey, skus, client, lookupFailed]);

  const summary = summarisePaste(parsed, known ? new Set(known.keys()) : null);
  /**
   * Resolved once, here, so the counter and the button promise exactly what the
   * confirm will add - including the de-duplication of a list that names a SKU
   * twice.
   */
  const resolution = useMemo(
    () =>
      known
        ? resolvePasteRows(
            summary.ready,
            known,
            Math.max(1, Number(minQuantity) || 1),
            existingRows,
          )
        : null,
     
    [known, summary.ready, minQuantity, existingRows],
  );
  const readyCount = resolution?.rows.length ?? 0;
  /**
   * MAX_TIER_PRICES is a cap on the create this save would send, not on one
   * paste, so the unsaved rows already on the grid count towards it. Two pastes
   * of 400 are one bulkCreate of 800 and the server refuses them together.
   */
  const pendingCreates = resolution
    ? existingRows.filter(row => !row.id && !resolution.replacedKeys.has(row.key)).length +
      resolution.rows.filter(row => !row.id).length
    : 0;
  const overCap = pendingCreates > PASTE_ROW_CAP;
  const handleConfirm = () => {
    if (!resolution) {
      return;
    }

    onConfirm(resolution.rows, resolution.replacedKeys);
    setText("");
    setResolved(null);
  };

  return (
    <ActionDialog
      open={open}
      // The stock modal width. A 960px dialog around a 600px form is dead space.
      size="sm"
      title={intl.formatMessage(pasteMessages.title)}
      confirmButtonState={resolving ? "loading" : "default"}
      confirmButtonLabel={
        readyCount
          ? intl.formatMessage(pasteMessages.confirm, { count: readyCount })
          : intl.formatMessage(pasteMessages.confirmEmpty)
      }
      disabled={!readyCount || resolving || overCap}
      onClose={onClose}
      onConfirm={handleConfirm}
    >
      <Box display="grid" gap={4} data-test-id="wsm-dealer-paste-dialog">
        <Text size={3} color="default2">
          {intl.formatMessage(pasteMessages.body)}
        </Text>
        <Textarea
          rows={8}
          value={text}
          // Monospace, so a pasted price list lines up as the two columns it is.
          style={{ fontFamily: "monospace" }}
          placeholder={intl.formatMessage(pasteMessages.placeholder)}
          onChange={event => setText(event.target.value)}
          data-test-id="wsm-dealer-paste-input"
        />
        <Box display="grid" __gridTemplateColumns="180px 1fr" gap={4} alignItems="center">
          <Input
            type="number"
            size="small"
            label={intl.formatMessage(pasteMessages.minQuantityLabel)}
            value={minQuantity}
            onChange={event => setMinQuantity(event.target.value)}
            data-test-id="wsm-dealer-paste-min-quantity"
          />
          <Text size={3} data-test-id="wsm-dealer-paste-count">
            {resolving
              ? intl.formatMessage(pasteMessages.resolving)
              : intl.formatMessage(pasteMessages.parsed, { count: readyCount })}
          </Text>
        </Box>
        {lookupFailed === skuKey && !resolving && (
          <Box display="grid" gap={2} data-test-id="wsm-dealer-paste-lookup-error">
            <Text size={3} color="critical1">
              {intl.formatMessage(pasteMessages.lookupFailed)}
            </Text>
            <Box>
              <Button
                variant="secondary"
                onClick={() => setLookupFailed(null)}
                data-test-id="wsm-dealer-paste-retry"
              >
                {intl.formatMessage(pasteMessages.retry)}
              </Button>
            </Box>
          </Box>
        )}
        {overCap && (
          <Text size={3} color="critical1" data-test-id="wsm-dealer-paste-cap">
            {intl.formatMessage(pasteMessages.cap, {
              count: PASTE_ROW_CAP,
              pending: pendingCreates,
            })}
          </Text>
        )}
        {summary.refused.length > 0 && (
          <Box display="grid" gap={2} data-test-id="wsm-dealer-paste-refusals">
            <Text size={3} color="critical1">
              {intl.formatMessage(pasteMessages.refusedTitle, { count: summary.refused.length })}
            </Text>
            <Box display="grid" __gridTemplateColumns="60px 1fr 1fr" gap={2}>
              <Text size={2} color="default2">
                {intl.formatMessage(pasteMessages.colLine)}
              </Text>
              <Text size={2} color="default2">
                {intl.formatMessage(pasteMessages.colContent)}
              </Text>
              <Text size={2} color="default2">
                {intl.formatMessage(pasteMessages.colProblem)}
              </Text>
              {summary.refused.slice(0, 20).map(refusal => (
                <React.Fragment key={refusal.line}>
                  <Text size={2}>{refusal.line}</Text>
                  <Text size={2}>{refusal.raw}</Text>
                  <Text size={2}>{intl.formatMessage(reasonMessage[refusal.reason])}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </ActionDialog>
  );
};

export default WsmDealerPasteDialog;
